import { Copy, Gift, Share2 } from 'lucide-react';
import type { Reward } from '@cover/core/api/types.ts';
import { date, money } from '@cover/core/format.ts';
import { Badge, type Tone } from '@cover/core/ui/Badge.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card, CardBody, CardHeader } from '@cover/core/ui/Card.tsx';
import { Empty, Load } from '@cover/core/ui/States.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { useMyReferral } from '../api.ts';
import { PageTitle } from '../Layout.tsx';

const REWARD_STATUS: Record<Reward['status'], { label: string; tone: Tone }> = {
  held: { label: 'Being checked', tone: 'neutral' },
  pending: { label: 'Waiting for their cover to start', tone: 'info' },
  qualified: { label: 'On its way', tone: 'info' },
  paid: { label: 'Sent', tone: 'ok' },
  rejected: { label: 'Not eligible', tone: 'neutral' },
  void: { label: 'Cancelled', tone: 'neutral' },
};

export function ReferPage() {
  const q = useMyReferral();
  const toast = useToast();
  return (
    <>
      <PageTitle>Refer a mate</PageTitle>
      <Load q={q}>
        {(r) => {
          if (!r.eligible || !r.shareUrl)
            return (
              <Card>
                <Empty icon={<Gift className="h-8 w-8" aria-hidden />} title="Your link arrives with your policy">
                  Once your cover has started, you’ll get a link to share with other tradespeople.
                </Empty>
              </Card>
            );
          // Reward wording is a financial promotion: only show it once it's approved.
          const offer = r.copyStatus === 'approved' && r.offer ? r.offer : 'Know another tradesperson who needs cover? Send them your link and we’ll look after them.';
          const shareText = `I sort my business insurance through WhatsApp with this lot. Have a look: ${r.shareUrl}`;
          async function copy() {
            try {
              await navigator.clipboard.writeText(r.shareUrl!);
              toast.ok('Link copied.');
            } catch {
              toast.fail(new Error('Couldn’t copy. Press and hold the link to copy it.'));
            }
          }
          async function share() {
            if (navigator.share) await navigator.share({ text: shareText }).catch(() => undefined);
            else window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
          }
          return (
            <div className="space-y-5">
              <Card>
                <CardBody className="space-y-4">
                  <p className="text-lg">{offer}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="min-w-0 flex-1 select-all break-all rounded-md bg-canvas px-3 py-2.5 text-sm">{r.shareUrl}</code>
                    <Button size="sm" variant="secondary" onClick={copy}>
                      <Copy className="h-4 w-4" aria-hidden /> Copy
                    </Button>
                  </div>
                  <Button size="full" onClick={share}>
                    <Share2 className="h-5 w-5" aria-hidden /> Share in WhatsApp
                  </Button>
                  {r.code && <p className="text-sm text-mid">Or they can mention code <strong className="font-mono">{r.code}</strong> when they message us.</p>}
                </CardBody>
              </Card>
              <Card>
                <CardHeader title="Your rewards" />
                <CardBody>
                  {r.rewards?.length ? (
                    <ul className="divide-y divide-line">
                      {r.rewards.map((w) => (
                        <li key={w.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                          <div>
                            <p className="font-semibold">{money(w.amountPence)} voucher</p>
                            <p className="text-sm text-mid">{w.paidAt ? `Sent ${date(w.paidAt)}` : w.createdAt ? `Earned ${date(w.createdAt)}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge tone={REWARD_STATUS[w.status].tone}>{REWARD_STATUS[w.status].label}</Badge>
                            {w.status === 'paid' && w.redeemUrl && (
                              <a href={w.redeemUrl} target="_blank" rel="noopener" className="text-sm font-semibold">
                                Use it
                              </a>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-mid">No rewards yet.</p>
                  )}
                </CardBody>
              </Card>
            </div>
          );
        }}
      </Load>
    </>
  );
}
